import { isNullOrUndefined, isFloatGreaterThan, isValidFunction, isObject, isValidNumber, isValidString, isNoEmptyArray } from '@pineapplelab/util';
import { __awaiter } from 'tslib';

const metadata = {
    dbRef: null,
    pagingService: null
};

var PagingDataType;
(function (PagingDataType) {
    PagingDataType[PagingDataType["number"] = 1] = "number";
    PagingDataType[PagingDataType["string"] = 0] = "string";
    PagingDataType[PagingDataType["timestamp"] = 2] = "timestamp";
    /**
     * This type will be used only in local paging.
     * The field inside the array must be a string or number.
     * Nested arrays are not supported.
     */
    PagingDataType[PagingDataType["array"] = 3] = "array";
})(PagingDataType || (PagingDataType = {}));

var PagingOrderType;
(function (PagingOrderType) {
    PagingOrderType[PagingOrderType["desc"] = 1] = "desc";
    PagingOrderType[PagingOrderType["asc"] = 0] = "asc";
})(PagingOrderType || (PagingOrderType = {}));

var PagingAction;
(function (PagingAction) {
    PagingAction[PagingAction["nextPage"] = 1] = "nextPage";
    PagingAction[PagingAction["previousPage"] = 0] = "previousPage";
})(PagingAction || (PagingAction = {}));

class PagingService {
    constructor() { }
    getData(paging) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (paging.orderBy === undefined || paging.orderBy === null || paging.orderBy === '') {
                paging.orderBy = paging.keyField;
            }
            const response = { data: [], hasLess: false, hasMore: false };
            let ref = metadata.dbRef;
            if (!isNullOrUndefined(ref.firestore)) {
                ref = ref.firestore;
            }
            paging.subCollections.forEach(subCollection => {
                ref = ref.collection(subCollection.collection).doc(subCollection.docKey);
            });
            ref = ref.collection(paging.collection);
            paging.preconditions.forEach(condition => {
                ref = ref.where(condition.fieldPath, condition.filterOp, condition.value);
                if (condition.filterOp !== '==' && condition.filterOp !== 'in') {
                    ref = ref.orderBy(condition.fieldPath);
                }
            });
            ref = ref.orderBy(paging.orderBy, paging.orderType === PagingOrderType.asc ? 'asc' : 'desc');
            let refBeforeChangePage = null;
            // if data must be filtred
            if (paging.search !== undefined && paging.search !== null && paging.search !== '') {
                switch (paging.orderDataType) {
                    case PagingDataType.number: {
                        if (!isNaN(paging.search)) {
                            ref = ref.startAt(parseInt(paging.search)).endAt(parseInt(paging.search));
                        }
                        else {
                            ref = ref.startAt(paging.search).endAt(paging.search + '\uf8ff');
                        }
                        break;
                    }
                    case PagingDataType.string:
                    default: {
                        ref = ref.startAt(paging.search).endAt(paging.search + '\uf8ff');
                        break;
                    }
                }
            }
            // if data must change page
            if (!isNullOrUndefined(paging.action) || (!(paging === null || paging === void 0 ? void 0 : paging.hasLatestPagingParamsChanges()) && isFloatGreaterThan(paging.currentPage, 0) && !isNullOrUndefined(paging === null || paging === void 0 ? void 0 : paging.previousAfterData))) {
                switch (paging.action) {
                    case PagingAction.nextPage: {
                        ref = ref.startAfter(paging.after[paging.orderBy]);
                        response.hasLess = true;
                        break;
                    }
                    case PagingAction.previousPage: {
                        refBeforeChangePage = ref;
                        ref = ref.startAt(paging.before[paging.orderBy]);
                        break;
                    }
                    default: {
                        ref = ref.startAfter((_a = paging === null || paging === void 0 ? void 0 : paging.previousAfterData) === null || _a === void 0 ? void 0 : _a[paging.orderBy]);
                        response.hasLess = true;
                        break;
                    }
                }
            }
            //get data of this page
            let get;
            try {
                if (paging.unlimitedQueryResult) {
                    get = yield ref.get();
                }
                else {
                    get = yield ref.limit(paging.size).get();
                }
            }
            catch (e) {
                if (JSON.stringify(e).includes('FirebaseError') && e.message) {
                    throw new Error('FirebaseError: ' + e.message);
                }
                throw new Error('Error trying to get data');
            }
            response.data = yield Promise.all(get.docs.map((doc) => doc.data()));
            if (response.data.length === paging.size) {
                const nextItem = yield ref.startAfter(response.data[response.data.length - 1][paging.orderBy]).limit(1).get();
                response.hasMore = nextItem.docs.length > 0;
                if (refBeforeChangePage !== null) {
                    const previousItem = yield refBeforeChangePage.endBefore(response.data[0][paging.orderBy]).limit(1).get();
                    response.hasLess = previousItem.docs.length > 0;
                }
            }
            return response;
        });
    }
    getDataByDocsChanges(paging, docsInjector) {
        paging.loading = true;
        if (paging.orderBy === undefined || paging.orderBy === null || paging.orderBy === '') {
            paging.orderBy = paging.keyField;
        }
        const response = { data: [], hasLess: false, hasMore: false };
        let ref = metadata.dbRef;
        if (!isNullOrUndefined(ref.firestore)) {
            ref = ref.firestore;
        }
        paging.subCollections.forEach(subCollection => {
            ref = ref.collection(subCollection.collection).doc(subCollection.docKey);
        });
        ref = ref.collection(paging.collection);
        paging.preconditions.forEach(condition => {
            ref = ref.where(condition.fieldPath, condition.filterOp, condition.value);
            if (condition.filterOp !== '==') {
                ref = ref.orderBy(condition.fieldPath);
            }
        });
        ref = ref.orderBy(paging.orderBy, paging.orderType === PagingOrderType.asc ? 'asc' : 'desc');
        // if data must be filtered
        if (paging.search !== undefined && paging.search !== null && paging.search !== '') {
            switch (paging.orderDataType) {
                case PagingDataType.number: {
                    if (!isNaN(paging.search)) {
                        ref = ref.startAt(parseInt(paging.search)).endAt(parseInt(paging.search));
                    }
                    else {
                        ref = ref.startAt(paging.search).endAt(paging.search + '\uf8ff');
                    }
                    break;
                }
                case PagingDataType.string:
                default: {
                    ref = ref.startAt(paging.search).endAt(paging.search + '\uf8ff');
                    break;
                }
            }
        }
        const refBeforeChangePage = ref;
        // if data must change page
        if (paging.action !== undefined && paging.action !== null) {
            switch (paging.action) {
                case PagingAction.nextPage: {
                    ref = ref.startAfter(paging.after[paging.orderBy]);
                    response.hasLess = true;
                    break;
                }
                case PagingAction.previousPage: {
                    ref = ref.startAt(paging.before[paging.orderBy]);
                    break;
                }
                default: {
                    break;
                }
            }
        }
        paging.unsubscribeOnSnapshot();
        paging.before = null;
        paging.after = null;
        // get data of this page
        paging.onSnapshots.paging = ref /* firebase.firestore.Query */.limit(paging.size).onSnapshot((querySnapshot) => {
            paging.loading = false;
            querySnapshot.docChanges().map((documentChange) => {
                if (documentChange.oldIndex > -1) { // remove item
                    response.data.splice(documentChange.oldIndex, 1);
                }
                if (documentChange.newIndex > -1) { // new item
                    const data = documentChange.doc.data();
                    if (typeof docsInjector === 'function') {
                        docsInjector(data);
                    }
                    response.data.splice(documentChange.newIndex, 0, data);
                }
                if (documentChange.newIndex === (paging.size - 1)) { // check if the collection has more docs
                    paging.after = documentChange.doc.data();
                    paging.unsubscribeOnSnapshot('after');
                    paging.onSnapshots.after = refBeforeChangePage.startAfter(paging.after[paging.orderBy]).limit(1).onSnapshot((hasMoreSnapshot) => {
                        hasMoreSnapshot.docChanges().map((moreDocumentChange) => {
                            response.hasMore = moreDocumentChange.newIndex > -1;
                        });
                    });
                }
                if (documentChange.newIndex === 0) { // check if the collection has less docs
                    paging.unsubscribeOnSnapshot('before');
                    paging.pageHistory[paging.currentPage] = { after: null, before: documentChange.doc.data() };
                    paging.onSnapshots.before = refBeforeChangePage.endBefore(response.data[0][paging.orderBy]).limit(1).onSnapshot((hasLessSnapshot) => {
                        hasLessSnapshot.docChanges().map((lessDocumentChange) => {
                            var _a;
                            response.hasLess = lessDocumentChange.newIndex > -1;
                            if (response.hasLess && !isNullOrUndefined((_a = paging.pageHistory[paging.currentPage - 1]) === null || _a === void 0 ? void 0 : _a.before)) {
                                paging.before = paging.pageHistory[paging.currentPage - 1].before;
                            }
                            else {
                                paging.before = null;
                            }
                            response.hasLess = paging.before ? response.hasLess : false;
                        });
                    });
                }
            });
        }, (e) => {
            paging.loading = false;
            this.showPagingError(e);
        });
        return response;
    }
    showPagingError(error) {
        console.log(error);
        let message = 'Something is wrong!';
        if (error.message.includes('The query requires an index')) {
            message = 'Error, Index is required, tell it  to an admin';
        }
        // this._messagesService.showMessage(message, 8000);
    }
}

class PagingModule {
    static setFirestoreInstance(firestoreInstance) {
        metadata.dbRef = firestoreInstance;
        if (isNullOrUndefined(metadata.pagingService)) {
            metadata.pagingService = new PagingService();
        }
    }
}

class Paging {
    constructor(paging) {
        this.keyField = 'id';
        this.orderBy = 'name';
        this.sizeOptions = [10, 20, 40];
        this.size = this.sizeOptions[0];
        this.orderType = Paging.orderType.asc;
        this.orderDataType = Paging.dataType.string;
        this.after = null;
        this.before = null;
        this.action = null;
        this.search = '';
        this.orderOptions = [];
        this.preconditions = [];
        this.subCollections = [];
        this.response = { data: [], hasMore: false, hasLess: false };
        this.pagingSearchOptions = {
            noOrderOptions: false,
            placeholder: 'Write something',
        };
        this.pagingKey = null;
        this.loading = false;
        this.onSnapshots = { paging: null, after: null, before: null };
        this.currentPage = null;
        this.pageHistory = [];
        this.last = {
            search: this.search,
            orderBy: this.orderBy,
            size: this.size,
            orderType: this.orderType
        };
        this.__trueLoadData = () => __awaiter(this, void 0, void 0, function* () {
            if (isValidFunction(this.beforeLoadData)) {
                yield this.beforeLoadData();
            }
            if (isNullOrUndefined(metadata) || isNullOrUndefined(metadata.dbRef) || isNullOrUndefined(metadata.pagingService)) {
                throw new Error('Paging service not initialized');
            }
            this.loading = true;
            const response = yield metadata.pagingService.getData(this);
            this.paginate(response);
            const mappable = isValidFunction(this.mapElements);
            this.response.data = this.response.data.map((element, index, array) => {
                if (!mappable) {
                    return element;
                }
                return this.mapElements(element, index, array);
            });
            this.loading = false;
            if (isValidFunction(this.afterLoadData)) {
                yield this.afterLoadData();
            }
        });
        if (isObject(paging)) {
            delete paging.loadData;
            delete paging.__trueLoadData;
            delete paging.pageHistory;
            delete paging.last;
            delete paging.response;
            Object.assign(this, paging);
        }
        if (isValidFunction(paging.beforeLoadData)) {
            this.beforeLoadData = paging.beforeLoadData;
        }
        if (isValidFunction(paging.afterLoadData)) {
            this.afterLoadData = paging.afterLoadData;
        }
    }
    get currentPageDisplayed() {
        return isValidNumber(this.currentPage) ? this.currentPage + 1 : 1;
    }
    get previousAfterData() {
        var _a;
        return (_a = this.pageHistory[this.currentPage - 1]) === null || _a === void 0 ? void 0 : _a.after;
    }
    loadData() {
        return this.__trueLoadData();
    }
    paginate(response) {
        var _a;
        if ((this.currentPage === null && response != undefined && response.data.length > 0)
            || this.hasLatestPagingParamsChanges()) {
            this.pageHistory = [];
            this.currentPage = 0;
            this.pageHistory[this.currentPage] = { after: null, before: Object.assign({}, response.data[0]) };
        }
        else if (this.action === Paging.pagingAction.nextPage) {
            this.currentPage++;
            this.pageHistory[this.currentPage] = { after: null, before: Object.assign({}, response.data[0]) };
        }
        else if (this.action === Paging.pagingAction.previousPage) {
            this.currentPage--;
        }
        this.action = null;
        this.response = response;
        if ((response === null || response === void 0 ? void 0 : response.hasLess) && !isNullOrUndefined((_a = this.pageHistory[this.currentPage - 1]) === null || _a === void 0 ? void 0 : _a.before)) {
            this.before = Object.assign({}, this.pageHistory[this.currentPage - 1].before);
        }
        else {
            this.before = null;
        }
        if (response === null || response === void 0 ? void 0 : response.hasMore) {
            this.after = Object.assign({}, response.data[response.data.length - 1]);
        }
        else {
            this.after = null;
        }
        if (!isNullOrUndefined(this.pageHistory[this.currentPage])) {
            this.pageHistory[this.currentPage].after = this.after;
        }
        // put the last values
        this.last.search = this.search;
        this.last.orderBy = this.orderBy;
        this.last.orderType = this.orderType;
        this.last.size = this.size;
    }
    unsubscribeOnSnapshot(onSnapshot = null) {
        if (typeof this.onSnapshots.paging === 'function' && onSnapshot !== 'before' && onSnapshot !== 'after') {
            this.onSnapshots.paging();
            this.onSnapshots.paging = null;
        }
        if (typeof this.onSnapshots.before === 'function' && onSnapshot !== 'after') {
            this.onSnapshots.before();
            this.onSnapshots.before = null;
        }
        if (typeof this.onSnapshots.after === 'function' && onSnapshot !== 'before') {
            this.onSnapshots.after();
            this.onSnapshots.after = null;
        }
    }
    hasLatestPagingParamsChanges() {
        return this.last.search !== this.search || this.last.orderBy !== this.orderBy
            || this.last.orderType !== this.orderType || this.last.size !== this.size;
    }
}
(function (Paging) {
    Paging.dataType = PagingDataType;
    Paging.pagingAction = PagingAction;
    Paging.orderType = PagingOrderType;
})(Paging || (Paging = {}));

/**
 * A progressive paging allows to load data from the beginning to the end of the collection.
 *
 * You only need to call the loadMore() method when you see the end of the current list.
 */
class ProgressivePaging extends Paging {
    constructor() {
        super(...arguments);
        this.__historyByPage = {};
    }
    loadData() {
        const _super = Object.create(null, {
            loadData: { get: () => super.loadData }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (this.action === Paging.pagingAction.previousPage) {
                return;
            }
            yield _super.loadData.call(this);
            this.__historyByPage[this.currentPage] = this.response.data;
            const toData = [];
            for (let index = 0; index <= this.currentPage; index++) {
                toData.splice(toData.length, 0, ...this.__historyByPage[index] || []);
            }
            this.response.hasLess = false;
            this.response.data = toData;
        });
    }
    loadMore() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.response.hasMore) {
                return;
            }
            this.action = Paging.pagingAction.nextPage;
            yield this.loadData();
        });
    }
}

function checkIfSameDateOrInRange(value, toCompare) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const date = getDate(value);
    const dateToCompare = getDateOrRange(toCompare);
    if (isNullOrUndefined(date) || isNullOrUndefined(dateToCompare)) {
        return false;
    }
    if (dateToCompare instanceof Date) {
        return ((_a = date === null || date === void 0 ? void 0 : date.getTime) === null || _a === void 0 ? void 0 : _a.call(date)) === ((_b = dateToCompare === null || dateToCompare === void 0 ? void 0 : dateToCompare.getTime) === null || _b === void 0 ? void 0 : _b.call(dateToCompare));
    }
    const { start = null, end = null } = dateToCompare;
    if (isNullOrUndefined(start) && isNullOrUndefined(end)) {
        return false;
    }
    if (isNullOrUndefined(start)) {
        return ((_c = date === null || date === void 0 ? void 0 : date.getTime) === null || _c === void 0 ? void 0 : _c.call(date)) <= ((_d = end === null || end === void 0 ? void 0 : end.getTime) === null || _d === void 0 ? void 0 : _d.call(end));
    }
    if (isNullOrUndefined(end)) {
        return ((_e = date === null || date === void 0 ? void 0 : date.getTime) === null || _e === void 0 ? void 0 : _e.call(date)) >= ((_f = start === null || start === void 0 ? void 0 : start.getTime) === null || _f === void 0 ? void 0 : _f.call(start));
    }
    return ((_g = date === null || date === void 0 ? void 0 : date.getTime) === null || _g === void 0 ? void 0 : _g.call(date)) >= ((_h = start === null || start === void 0 ? void 0 : start.getTime) === null || _h === void 0 ? void 0 : _h.call(start)) && ((_j = date === null || date === void 0 ? void 0 : date.getTime) === null || _j === void 0 ? void 0 : _j.call(date)) <= ((_k = end === null || end === void 0 ? void 0 : end.getTime) === null || _k === void 0 ? void 0 : _k.call(end));
}
function getDateOrRange(value) {
    if (!isValidJson(value)) {
        return getDate(value);
    }
    let { start = null, end = null } = JSON.parse(value);
    start = getDate(start);
    end = getDate(end);
    return { start, end };
}
function getDate(value) {
    if (isNullOrUndefined(value)) {
        return null;
    }
    if (isValidNumber((value === null || value === void 0 ? void 0 : value.seconds) || (value === null || value === void 0 ? void 0 : value._seconds) || (value === null || value === void 0 ? void 0 : value.__seconds))) {
        return new Date(((value === null || value === void 0 ? void 0 : value.seconds) || (value === null || value === void 0 ? void 0 : value._seconds) || (value === null || value === void 0 ? void 0 : value.__seconds)) * 1000);
    }
    const date = new Date(value);
    return isNaN(date) ? null : date;
}
function isValidJson(value) {
    try {
        JSON.parse(value);
    }
    catch (e) {
        return false;
    }
    return true;
}

/**
 * A local paging will get all firebase documents that meet the defined preconditions and sort it in memory.
 *
 * If you want use your own data, call the `setLocalData` method.
 */
class LocalPaging extends Paging {
    constructor(paging) {
        super(paging);
        this.unlimitedQueryResult = true;
        this.__trueResponse = null;
        this.sortedData = [];
        this.orderByLocally = paging.orderByLocally;
        this.beforeLoadCollectionData = paging === null || paging === void 0 ? void 0 : paging.beforeLoadCollectionData;
        this.afterLoadCollectionData = paging === null || paging === void 0 ? void 0 : paging.afterLoadCollectionData;
        this.trueDataFilter = paging === null || paging === void 0 ? void 0 : paging.trueDataFilter;
    }
    /**
     * If you set the data, it will be used instead of the firebase data.
     *
     * @param data: T[] This is the all data that is used for sorting and paging.
     */
    setLocalData(data) {
        this.__trueResponse = { data, hasLess: false, hasMore: false };
    }
    loadData() {
        return __awaiter(this, void 0, void 0, function* () {
            this.loading = true;
            if (isValidFunction(this.beforeLoadData)) {
                yield this.beforeLoadData();
            }
            const response = yield this.__localLoadData();
            this.paginate(response);
            if (isValidFunction(this.afterLoadData)) {
                yield this.afterLoadData();
            }
            this.loading = false;
        });
    }
    refreshCollectionData() {
        return this.getCollectionData();
    }
    getCollectionData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (isValidFunction(this.beforeLoadCollectionData)) {
                yield this.beforeLoadCollectionData();
            }
            if (isNullOrUndefined(metadata) || isNullOrUndefined(metadata.dbRef) || isNullOrUndefined(metadata.pagingService)) {
                throw new Error('Paging service not initialized');
            }
            this.loading = true;
            const response = yield metadata.pagingService.getData(this);
            const mappable = isValidFunction(this.mapElements);
            response.data = response.data.map((element, index, array) => {
                if (!mappable) {
                    return element;
                }
                return this.mapElements(element, index, array);
            });
            this.loading = false;
            if (isNullOrUndefined(this.__trueResponse)) {
                this.__trueResponse = { data: [], hasLess: false, hasMore: false };
            }
            this.__trueResponse.data.splice(0, this.__trueResponse.data.length, ...response.data);
            this.sortedData = this.__trueResponse.data;
            if (isValidFunction(this.afterLoadCollectionData)) {
                yield this.afterLoadCollectionData();
            }
        });
    }
    __localLoadData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (isNullOrUndefined(this.__trueResponse)) {
                yield this.getCollectionData();
            }
            this.validatePaging();
            const orderBy = isValidString(this.orderByLocally) ? this.orderByLocally : this.orderBy || this.keyField;
            const orderType = !isNullOrUndefined(this.orderTypeLocally) ? this.orderTypeLocally : this.orderType;
            const secondOrderBy = isValidString(this.secondOrderByLocally) ? this.secondOrderByLocally : orderBy;
            const secondOrderType = !isNullOrUndefined(this.secondOrderTypeLocally) ? this.secondOrderTypeLocally : orderType;
            let data = this.__trueResponse.data;
            if (isValidFunction(this.trueDataFilter)) {
                data = this.__trueResponse.data.filter(this.trueDataFilter);
            }
            this.sortedData = data.sort((a, b) => {
                const result = this.sortHandler(a, b, orderBy, orderType);
                if (result === 0) {
                    return this.sortHandler(a, b, secondOrderBy, secondOrderType);
                }
                return result;
            });
            let filteredResult = null;
            if (!isNullOrUndefined(this.search)) {
                filteredResult = this.sortedData.filter(element => {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    switch (this.orderDataType) {
                        case Paging.dataType.timestamp:
                            return checkIfSameDateOrInRange(element[orderBy], this.search);
                            break;
                        case Paging.dataType.array:
                            const [arrayField, field] = orderBy.split('$');
                            if (isNullOrUndefined(arrayField) || isNullOrUndefined(field) || isNullOrUndefined(element)) {
                                return false;
                            }
                            const array = element[arrayField];
                            if (!isNoEmptyArray(array)) {
                                return false;
                            }
                            return ((_a = array === null || array === void 0 ? void 0 : array.some) === null || _a === void 0 ? void 0 : _a.call(array, item => { var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k; return item && ((_f = (_e = (_d = (_c = (_b = (_a = item[field]) === null || _a === void 0 ? void 0 : _a.toString) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.toLowerCase) === null || _d === void 0 ? void 0 : _d.call(_c)) === null || _e === void 0 ? void 0 : _e.includes) === null || _f === void 0 ? void 0 : _f.call(_e, (_k = (_j = (_h = (_g = this.search) === null || _g === void 0 ? void 0 : _g.toString) === null || _h === void 0 ? void 0 : _h.call(_g)) === null || _j === void 0 ? void 0 : _j.toLowerCase) === null || _k === void 0 ? void 0 : _k.call(_j))); })) || false;
                            break;
                        case Paging.dataType.number:
                        case Paging.dataType.string:
                        default: {
                            let item = element;
                            orderBy.split('.').forEach(field => item = isNullOrUndefined(item) ? null : item[field]);
                            return ((_f = (_e = (_d = (_c = (_b = item === null || item === void 0 ? void 0 : item.toString) === null || _b === void 0 ? void 0 : _b.call(item)) === null || _c === void 0 ? void 0 : _c.toLowerCase) === null || _d === void 0 ? void 0 : _d.call(_c)) === null || _e === void 0 ? void 0 : _e.includes) === null || _f === void 0 ? void 0 : _f.call(_e, (_h = (_g = this.search) === null || _g === void 0 ? void 0 : _g.toString()) === null || _h === void 0 ? void 0 : _h.toLowerCase())) || false;
                            break;
                        }
                    }
                });
            }
            if (isNullOrUndefined(filteredResult)) {
                filteredResult = this.sortedData.slice(0);
            }
            let currentPage = this.hasLatestPagingParamsChanges() ? 0 : this.currentPage;
            if (this.action === Paging.pagingAction.nextPage) {
                currentPage += 1;
            }
            else if (this.action === Paging.pagingAction.previousPage) {
                currentPage -= 1;
            }
            const response = { data: [], hasLess: false, hasMore: false };
            if (filteredResult.length > (this.size * (currentPage + 1))) {
                response.hasMore = true;
            }
            const startIndex = currentPage * this.size;
            filteredResult = filteredResult.slice(startIndex, startIndex + this.size);
            response.data = filteredResult;
            if (currentPage > 0) {
                response.hasLess = true;
            }
            return response;
        });
    }
    validatePaging() {
        if (!isValidNumber(this.currentPage)) {
            this.currentPage = 0;
        }
        if (!isValidNumber(this.size)) {
            throw new Error('Size must be a number');
        }
    }
    sortHandler(a, b, orderBy, orderType) {
        var _a, _b, _c, _d;
        if ((_a = orderBy === null || orderBy === void 0 ? void 0 : orderBy.includes) === null || _a === void 0 ? void 0 : _a.call(orderBy, '$')) {
            return 0;
        }
        orderBy === null || orderBy === void 0 ? void 0 : orderBy.split('.').slice(0, -1).map(field => {
            a = isNullOrUndefined(a) ? null : a[field];
            b = isNullOrUndefined(b) ? null : b[field];
        });
        if (isNullOrUndefined(a) || isNullOrUndefined(b)) {
            return 0;
        }
        orderBy = (_d = (_c = (_b = orderBy === null || orderBy === void 0 ? void 0 : orderBy.split) === null || _b === void 0 ? void 0 : _b.call(orderBy, '.')) === null || _c === void 0 ? void 0 : _c.pop) === null || _d === void 0 ? void 0 : _d.call(_c);
        if (a[orderBy] == b[orderBy]) {
            return 0;
        }
        if (a[orderBy] > b[orderBy]) {
            return orderType === Paging.orderType.asc ? 1 : -1;
        }
        return orderType === Paging.orderType.asc ? -1 : 1;
    }
}

/*
 * Public API Surface of paging
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LocalPaging, Paging, PagingAction, PagingDataType, PagingModule, PagingOrderType, ProgressivePaging };
